import axios from 'axios';
import { setupAuthInterceptor, retryWithBackoff } from '../common';
import { NAMESPACES } from '../configuration';

const BASE_URL = NAMESPACES.menu_api.item_stats;

// Función auxiliar para configurar la instancia de Axios
const api = setupAuthInterceptor(axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
}));


// --- TYPE DEFINITIONS ---

type AggregationType = 
  | 'by_item' 
  | 'by_user' 
  | 'by_third_party' 
  | 'by_pedido_type' 
  | 'by_menu_category' 
  | 'by_menu_category_type';


// --- API FUNCTIONS ---

/**
 * Creates a pre-aggregated record for an entire month.
 * This is typically called by a scheduled background job.
 */
export const CreateMonthlyRecord = (data: {
  month: string; // Format: YYYY-MM
  local_id: number;
}) => {
  return retryWithBackoff(() => api.post('/CreateMonthlyRecord', data));
};

/**
 * Retrieves a pre-aggregated record for a single month.
 * If the record doesn't exist and the month is in the past,
 * it will be computed and cached on the fly by the backend.
 */
export const GetMonthlyRecord = (data: {
  month: string; // Format: YYYY-MM
  local_id: number;
  aggregation_type?: AggregationType;
}) => {
  return retryWithBackoff(() => api.post('/GetMonthlyRecord', data));
};

/**
 * The main endpoint for querying sales data over a specific date range.
 * It intelligently uses monthly and daily records for efficiency.
 */
export const GetStatsByRange = (data: {
  local_id: number;
  start_date: string; // Format: YYYY-MM-DD
  end_date: string;   // Format: YYYY-MM-DD
  aggregation_type?: AggregationType;
}) => {
  return retryWithBackoff(() => api.post('/GetStatsByRange', data));
};

/**
 * Creates a pre-aggregated record for a single day's session.
 * This can be called to backfill data or at the end of a business day.
 */
export const CreateSessionRecord = (session_id: number) => {
  return retryWithBackoff(() => api.post(`/CreateSessionRecord/${session_id}`));
};

/**
 * Retrieves aggregated stats for a single, specific session ID.
 * This endpoint uses a cache; it computes stats once and serves from cache after.
 */
export const GetAggregatedStats = (data: {
    session_id: number;
    aggregation_type: AggregationType;
}) => {
    const { session_id, ...body } = data;
    return retryWithBackoff(() => api.post(`/GetAggregatedStats/${session_id}`, body));
};


/**
 * A convenience endpoint to get stats for the current active session for a local.
 * If no session is active, it falls back to the most recently closed one.
 */
export const GetStatsForCurrentSession = (data: {
    local_id: number;
    aggregation_type: AggregationType;
  }) => {
    return retryWithBackoff(() => api.post('/GetStatsForCurrentSession', data));
  };
